import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, BarChart2, BookOpenCheck, ExternalLink, Award, TrendingUp, Shield, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getExams, getCoursesWithProgress, getUserStats, updateUserProfile } from '../services/api';
import { Exam, Course, UserStats } from '../types';
import Logo from './Logo';
import { CourseViewer } from './CourseViewer';
import ProfileCompletionModal from './ProfileCompletionModal';

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [availableExams, setAvailableExams] = useState<Exam[]>([]);
  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [userStats, setUserStats] = useState<UserStats>({
    examsTaken: 0,
    averageScore: 0,
    highestScore: 0,
    lowestScore: 0,
    coursesStarted: 0,
    coursesCompleted: 0,
    averageCourseProgress: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const userRef = useRef(user);
  userRef.current = user;

  const fetchData = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const [examsResponse, coursesResponse, statsResponse] = await Promise.all([
        getExams(),
        getCoursesWithProgress(),
        getUserStats(currentUser.id)
      ]);

      setAvailableExams(examsResponse.data || []);
      setMyCourses(coursesResponse.data || []);
      setUserStats({
        examsTaken: Number(statsResponse.data.examsTaken) || 0,
        averageScore: Number(statsResponse.data.averageScore) || 0,
        highestScore: Number(statsResponse.data.highestScore) || 0,
        lowestScore: Number(statsResponse.data.lowestScore) || 0,
        coursesStarted: Number(statsResponse.data.coursesStarted) || 0,
        coursesCompleted: Number(statsResponse.data.coursesCompleted) || 0,
        averageCourseProgress: Number(statsResponse.data.averageCourseProgress) || 0
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);

      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to load data. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.branch || !user?.department) {
      setShowProfileModal(true);
    } else {
      setShowProfileModal(false);
    }
  }, [user?.branch, user?.department]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    } else {
      setIsLoading(false);
    }

    const handleFocus = () => {
      if (userRef.current?.id) fetchData();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden && userRef.current?.id) fetchData();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, fetchData]);

  const handleProfileCompletion = async (profileData: { branch: string; department: string }) => {
    if (!user?.id) return;
    
    try {
      const response = await updateUserProfile(user.id, profileData);
      
      // Update user context with new profile data using the auth context method
      updateUser({
        branch: response.data.user.branch,
        department: response.data.user.department
      });
      
      setShowProfileModal(false);
    } catch (error: any) {
      console.error('Error completing profile:', error);
      throw error;
    }
  };

  const handleCourseClick = (course: Course) => {
    setSelectedCourse(course);
    // Scroll to top when course is selected
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Award className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <TrendingUp className="w-3 h-3 mr-1" />
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Not Started
          </span>
        );
    }
  };

  if (selectedCourse) {
    return (
      <CourseViewer 
        course={selectedCourse} 
        onClose={() => {
          setSelectedCourse(null);
          // Scroll to top when returning to dashboard
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Refresh dashboard data to show updated progress
          console.log('[Dashboard] Course closed, refreshing data...');
          if (user?.id) {
            // Trigger a data refresh
            const refreshData = async () => {
              try {
                const [coursesResponse, statsResponse] = await Promise.all([
                  getCoursesWithProgress(),
                  getUserStats(user.id)
                ]);
                setMyCourses(coursesResponse.data || []);
                setUserStats({
                  examsTaken: Number(statsResponse.data.examsTaken) || 0,
                  averageScore: Number(statsResponse.data.averageScore) || 0,
                  highestScore: Number(statsResponse.data.highestScore) || 0,
                  lowestScore: Number(statsResponse.data.lowestScore) || 0,
                  coursesStarted: Number(statsResponse.data.coursesStarted) || 0,
                  coursesCompleted: Number(statsResponse.data.coursesCompleted) || 0,
                  averageCourseProgress: Number(statsResponse.data.averageCourseProgress) || 0
                });
                console.log('[Dashboard] Data refreshed successfully');
              } catch (error) {
                console.error('[Dashboard] Error refreshing data:', error);
              }
            };
            refreshData();
          }
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 sm:mb-8 gap-4">
        <Logo className="h-10 sm:h-12" />
        <div className="text-center sm:text-right">
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-400" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Mascott Learning Hub
          </h1>
          <h2 className="text-lg sm:text-xl text-gray-400 mt-1 sm:mt-2">Welcome, {user?.name || 'User'}!</h2>
        </div>
      </div>

      {error && (
        <div className="bg-red-500 text-white p-4 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {/* Statistics and Certification Banner */}
      <div className="mb-8">
        {/* Statistics Row */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-6">
          {/* Exam Statistics */}
          <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center">
              <BarChart2 className="mr-2 w-5 h-5" /> Exam Statistics
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Exams Taken</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-400">{userStats.examsTaken}</p>
              </div>
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Average Score</p>
                <p className="text-xl sm:text-2xl font-bold text-green-400">
                  {userStats.averageScore.toFixed(1)}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Highest Score</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-400">
                  {userStats.highestScore.toFixed(1)}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Lowest Score</p>
                <p className="text-xl sm:text-2xl font-bold text-red-400">
                  {userStats.lowestScore.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Course Statistics */}
          <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center">
              <BookOpenCheck className="mr-2 w-5 h-5" /> Course Statistics
            </h2>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Started</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-400">{userStats.coursesStarted}</p>
              </div>
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-green-400">{userStats.coursesCompleted}</p>
              </div>
              <div className="text-center">
                <p className="text-xs sm:text-sm font-medium text-gray-400">Avg Progress</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-400">
                  {userStats.averageCourseProgress.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Certification Tracking */}
        <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center">
            <Shield className="mr-2 w-5 h-5" /> Certification Tracking
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <div 
              className="bg-gray-700 p-3 sm:p-4 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
              onClick={() => navigate('/technician/profile')}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold mb-1 flex items-center">
                    <Award className="mr-2 text-blue-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span className="truncate">My Certifications</span>
                  </h3>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    View your certification progress, tier level, and upload certificates
                  </p>
                </div>
                <ExternalLink className="text-blue-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ml-2" />
              </div>
            </div>
            
            {user?.is_admin && (
              <div 
                className="bg-gray-700 p-3 sm:p-4 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                onClick={() => navigate('/manager/dashboard')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold mb-1 flex items-center">
                      <Users className="mr-2 text-purple-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <span className="truncate">Team Management</span>
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      Oversee team certification compliance and progress
                    </p>
                  </div>
                  <ExternalLink className="text-purple-400 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ml-2" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content: Exams and Courses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Available Exams - Left Side */}
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 flex items-center">
            <BookOpen className="mr-2 w-5 h-5 sm:w-6 sm:h-6" /> Available Exams
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {availableExams.length === 0 ? (
              <div className="bg-gray-800 p-4 rounded-lg text-center text-gray-400">
                No exams available at this time.
              </div>
            ) : (
              availableExams.map((exam) => (
                <div key={exam.id} className="bg-gray-800 p-4 rounded-lg shadow">
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{exam.name}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 sm:mb-4">
                    <p className="text-gray-400 text-sm">Time Limit: {exam.time_limit} minutes</p>
                    <p className="text-gray-400 text-sm">Questions: {exam.total_questions}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/exam/${exam.id}`)}
                    className="w-full sm:w-auto bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 transition duration-300 flex items-center justify-center"
                  >
                    Start Exam <ExternalLink className="ml-2 h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* My Courses - Right Side */}
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 flex items-center">
            <BookOpenCheck className="mr-2 w-5 h-5 sm:w-6 sm:h-6" /> My Courses
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {myCourses.length === 0 ? (
              <div className="bg-gray-800 p-4 rounded-lg text-center text-gray-400">
                No courses available at this time.
              </div>
            ) : (
              myCourses.map((course) => (
                <div key={course.id} className="bg-gray-800 p-4 rounded-lg shadow">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                    <h3 className="text-lg sm:text-xl font-semibold flex-1">{course.title}</h3>
                    <div className="flex-shrink-0">
                      {getStatusBadge(course.status || 'not-started')}
                    </div>
                  </div>
                  <p className="text-gray-400 mb-2 text-sm">
                    Modules: {course.modules?.length || 0}
                  </p>
                  {course.progress !== undefined && course.progress > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-400 mb-1">
                        <span>Progress</span>
                        <span>{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => handleCourseClick(course)}
                    className="w-full sm:w-auto bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-500 transition duration-300 flex items-center justify-center"
                  >
                    View Course <ExternalLink className="ml-2 h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onComplete={handleProfileCompletion}
        onClose={() => {}} // Don't allow closing without completing
      />
    </div>
  );
};

export default UserDashboard;